import { Trans } from '@lingui/macro';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import styled from 'styled-components';

import { addDaysToCurrentDate } from '../../helpers/dateUtils';
import {
  ensureReactDatetimeA11yEnhancements,
  renderAccessibleDatetimeDay
} from '@components/library/date_time_picker/reactDatetimeA11y';

import { PrivacySettingsColors } from './colors';
import { ExpirationDateSectionProps } from './types';

// ===== STYLED COMPONENTS =====

const ExpirationSection = styled.div`
  margin-top: 10px;
`;

const ExpirationHeading = styled.div`
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: bold;
  }
`;

const ExpirationBody = styled.div`
  > * {
    margin-bottom: 10px;
  }
`;

const ExpirationOption = styled.div`
  display: flex;
  align-items: center;

  input[type="radio"] {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid ${PrivacySettingsColors.FormBorder};
    border-radius: 50%;
    margin: 0 10px 0 0;
    position: relative;
    background-color: ${PrivacySettingsColors.White};

    &:checked {
      border-color: ${PrivacySettingsColors.PurplePopsicle};
      border-width: 6px;
    }
  }

  label {
    cursor: pointer;
    margin: unset;
    font-size: 14px;
    color: ${PrivacySettingsColors.FormText};
  }
`;

const CustomDateOption = styled.div`
  input[type="radio"] {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid ${PrivacySettingsColors.FormBorder};
    border-radius: 50%;
    margin: 0 10px 0 0;
    position: relative;
    background-color: ${PrivacySettingsColors.White};

    &:checked {
      border-color: ${PrivacySettingsColors.FocusBorder};
      border-width: 6px;
    }
  }

  label {
    cursor: pointer;
    font-size: 14px;
    color: ${PrivacySettingsColors.FormText};
    margin: 0;
  }
`;

const DatePickerContainer = styled.div`
  width: 250px;
  position: relative;
  margin-top: 10px;

  input {
    width: 100%;
    cursor: pointer;
  }

  .icon {
    position: absolute;
    top: 50%;
    right: 5%;
    transform: translateY(-50%);
    z-index: 1;
    pointer-events: none;
  }

  .rdtPicker {
    position: absolute;
    left: 0;
    bottom: 40px;
    font-size: 14px;
    margin-left: 10px;
    margin-bottom: 10px;
    z-index: 10001;
  }
`;

// ===== HELPER FUNCTIONS =====

/**
 * Validates if a date is within the allowed range
 */
const isDateInValidRange = (date: moment.Moment): boolean => {
  const today = moment().startOf('day');
  const tenYearsFromToday = moment().add(10, 'years').endOf('day');

  // Must be today or later, and not more than 10 years from now
  return date.isSameOrAfter(today) && date.isSameOrBefore(tenYearsFromToday);
};

// ===== MAIN COMPONENT =====

/**
 * ExpirationDateSection Component
 * Handles expiration date selection with preset options and custom date picker
 * Provides preset options and custom date picker for passkey expiration
 */
const ExpirationDateSection: React.FunctionComponent<ExpirationDateSectionProps> = ({
  expirationOptions,
  expirationOption,
  customDate,
  onExpirationChange,
  onCustomDateChange
}) => {
  // Internal state to track input value separately from valid date
  const [inputValue, setInputValue] = useState<string>('');

  // Update internal input value when customDate prop changes
  useEffect(() => {
    if (customDate) {
      const formattedDate = moment.isMoment(customDate)
        ? customDate.format('MM/DD/YYYY')
        : moment(customDate).format('MM/DD/YYYY');
      setInputValue(formattedDate);
    } else {
      setInputValue('');
    }
  }, [customDate]);

  useEffect(() => {
    ensureReactDatetimeA11yEnhancements();
  }, []);

  /**
   * Formats a future date for display in expiration option labels
   */
  const formatDateOptions = (daysToAdd: number): string => {
    return addDaysToCurrentDate(daysToAdd);
  };

  /**
   * Validates date and updates parent state only if valid
   */
  const validateAndSetDate = (dateString: string): void => {
    if (!dateString) {
      onCustomDateChange('');
      return;
    }

    const parsed = moment(dateString, 'MM/DD/YYYY', true);

    // Only update parent state if date is valid and in range
    if (parsed.isValid() && isDateInValidRange(parsed)) {
      onCustomDateChange(parsed);
    }
    // Note: We don't call onCustomDateChange for invalid dates
    // This keeps the last valid date in the parent state
  };

  /**
   * Validates date on blur and reverts input if invalid
   */
  const handleDateInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const input = e.target.value;

    if (!input) {
      setInputValue('');
      onCustomDateChange('');
      return;
    }

    const parsed = moment(input, 'MM/DD/YYYY', true);

    if (!parsed.isValid()) {
      // Clear invalid input instead of reverting to potentially invalid customDate
      setInputValue('');
      onCustomDateChange('');
      return;
    }

    const isInRange = isDateInValidRange(parsed);

    // If valid and within range, update the parent state
    if (isInRange) {
      onCustomDateChange(parsed);
      setInputValue(parsed.format('MM/DD/YYYY')); // Ensure consistent formatting
    } else {
      // Invalid input - clear the input instead of reverting to potentially invalid customDate
      setInputValue('');
      onCustomDateChange('');
    }
  };

  /**
   * Handles calendar date selection (always valid) - with extra validation
   */
  const handleCalendarChange = (value: string | moment.Moment): void => {
    if (moment.isMoment(value)) {
      // Double-check calendar selection is valid (should always be due to isValidDate)
      if (isDateInValidRange(value)) {
        const formattedDate = value.format('MM/DD/YYYY');
        setInputValue(formattedDate);
        onCustomDateChange(value);
      }
    } else if (typeof value === 'string') {
      // This shouldn't happen with calendar, but handle it with validation
      setInputValue(value);
      validateAndSetDate(value);
    }
  };

  /**
   * Handles manual input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setInputValue(value);
    // Don't validate on every keystroke, just update internal state
  };

  return (
    <ExpirationSection>
      <ExpirationHeading>
        <h3>
          <Trans>Expiration Date</Trans>
        </h3>
      </ExpirationHeading>
      <ExpirationBody>
        {expirationOptions.map((option) => (
          <ExpirationOption key={option.id}>
            <input
              checked={expirationOption === option.id}
              id={option.id}
              name="expiration_option"
              onChange={onExpirationChange}
              type="radio"
              value={option.id}
            />
            <label htmlFor={option.id}>
              {option.label} ({formatDateOptions(option.days)})
            </label>
          </ExpirationOption>
        ))}

        <CustomDateOption>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              checked={expirationOption === 'custom_date'}
              id="custom_date"
              name="expiration_option"
              onChange={onExpirationChange}
              type="radio"
              value="custom_date"
            />
            <label htmlFor="custom_date"><Trans>Custom date</Trans></label>
          </div>
          {expirationOption === 'custom_date' && (
            <DatePickerContainer>
              <Datetime
                className="custom-date-input"
                closeOnSelect={true}
                dateFormat="MM/DD/YYYY"
                inputProps={{
                  className: 'bf-input',
                  placeholder: 'MM/DD/YYYY',
                  value: inputValue,
                  onChange: handleInputChange,
                  onBlur: handleDateInputBlur,
                }}
                isValidDate={(current) => {
                  return isDateInValidRange(current);
                }}
                onChange={handleCalendarChange}
                renderDay={renderAccessibleDatetimeDay}
                strictParsing={true}
                timeFormat={false}
                value={customDate || undefined}
              />
              <span className="bff-calendar icon" />
            </DatePickerContainer>
          )}
        </CustomDateOption>
      </ExpirationBody>
    </ExpirationSection>
  );
};

export default ExpirationDateSection;
